// [PURPOSE]: State-driven JSX equivalent of the inline custom-roll-table
//            dialog that previously lived in App.jsx as ~200 lines of
//            imperative `document.createElement` + `appendChild` blocks
//            (with `document.getElementById` reads on save).
// [WHY]: Phase 2 audit S-13 (Batch 1.5) — the H-7 fix made the imperative
//        DOM safe via `textContent` assignments, but the architecture was
//        fragile: any future maintainer who swapped `textContent` for
//        `innerHTML` would silently re-introduce stored XSS via party-shared
//        item names (the original H-7 vector). React JSX text auto-escapes,
//        so the equivalent state-driven component is XSS-safe by construction.
// [INTEGRATION]: Imported by src/App.jsx, replacing the inline dialog block.

import React, { useState, useCallback } from 'react';
import MobileModal from './MobileModal';
// [PHASE B / #006 — B3-4]: Validation helpers extracted so production
//   and tests share one module (Rule 22). The diagnostic builder lets
//   the alert name every offending row + field instead of the prior
//   generic "fill in at least one table entry" message.
import {
  validateRollTableEntries,
  buildEntryDiagnostic,
} from '../utils/rollTableValidation';
// [PHASE B / #033 — 2026-05-06]: Stamp the {kind, ref, qty} schema on
//   the validated entries before persisting, so newly authored tables
//   carry the discriminator immediately. The roll-time migrator at
//   shopGenerator.rollFromTable() also covers any pre-#033 tables —
//   this just means new tables don't re-incur the migration cost on
//   every roll.
import { migrateAllEntries } from '../utils/rollTableEntrySchema';

const RARITIES = [
  { value: 'common', label: 'Common' },
  { value: 'uncommon', label: 'Uncommon' },
  { value: 'rare', label: 'Rare' },
  { value: 'very_rare', label: 'Very Rare' },
  { value: 'legendary', label: 'Legendary' },
];

function emptyEntry() {
  return { min: '', max: '', item: '', rarity: 'common' };
}

export default function CustomRollTableDialog({
  open,
  onClose,
  onCreate,
  inventory = [],
  baseItems = {},
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [entries, setEntries] = useState([emptyEntry()]);
  // [PHASE B / #025 — 2026-05-06]: Container contents are opt-in. User
  //   report 2026-05-06: "not being able to minimize containers makes
  //   it crazy hard to navigate the scroll area." Phase B / #018
  //   added `containedItems` inline to the picker which made the
  //   dropdown 30+ flat rows for inventories with multiple containers.
  //   Default OFF here keeps the picker short; toggle ON to surface
  //   the per-container `<optgroup>`s when the author wants to roll
  //   for a specific contained item.
  const [showContainerContents, setShowContainerContents] = useState(false);

  // Containers with at least one contained item — drives both whether
  // we render the toggle (no need if no containers have contents) and
  // the optgroup rendering when the toggle is on.
  const containersWithContents = inventory
    .map((item, idx) => ({ item, idx }))
    .filter(
      ({ item }) =>
        Array.isArray(item?.containedItems) && item.containedItems.length > 0
    );

  const handleAddEntry = useCallback(() => {
    setEntries((prev) => [...prev, emptyEntry()]);
  }, []);

  const handleRemoveEntry = useCallback((idx) => {
    setEntries((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const handleEntryChange = useCallback((idx, field, value) => {
    setEntries((prev) => prev.map((entry, i) => (i === idx ? { ...entry, [field]: value } : entry)));
  }, []);

  // [PHASE B / #049 — 2026-05-07]: Distribute 1-100 evenly across
  //   the current entries. Mirrors RollTablesTab's
  //   distributeRangesEvenly (src/components/tabs/RollTablesTab.js:369).
  //   The last entry extends to 100 to absorb any rounding remainder,
  //   so ranges always cover the full d100 range (no gaps that would
  //   trip rollOnTable's defensive fallback).
  const handleDistributeEvenly = useCallback(() => {
    setEntries((prev) => {
      const n = prev.length;
      if (n < 2) return prev;
      const step = Math.floor(100 / n);
      return prev.map((entry, i) => ({
        ...entry,
        min: String(i * step + 1),
        max: String(i === n - 1 ? 100 : (i + 1) * step),
      }));
    });
  }, []);

  const handleSave = useCallback(() => {
    if (!name.trim()) {
      // eslint-disable-next-line no-alert
      window.alert('Please provide a table name');
      return;
    }

    // [PHASE B / #006 — B3-4]: Per-entry validation + actionable error.
    //   When the user reports the alert "fires despite all entries
    //   having values" the underlying cause is almost always one of:
    //     (a) The Item <select> is on its empty placeholder
    //         "Select Item..." (value `''` is falsy)
    //     (b) min or max is empty/blank (parseInt('') is NaN)
    //     (c) A second row was added with + Add Entry but never filled.
    //   The diagnostic names every offending row + missing field so the
    //   user can fix it without guessing.
    const { validations, validRolls } = validateRollTableEntries(entries);

    if (validRolls.length === 0) {
      const diagnostic = buildEntryDiagnostic(validations);
      // eslint-disable-next-line no-alert
      window.alert(
        diagnostic
          ? `Please fill in at least one table entry with min, max, and item values.\n\n${diagnostic}`
          : 'Please fill in at least one table entry with min, max, and item values.'
      );
      return;
    }

    // [PHASE B / #033]: Persist with `kind`/`ref`/`qty` already stamped.
    //   `validRolls` come from the editor in the legacy
    //   `{min, max, item, rarity}` shape; the migrator upgrades them
    //   in-place. Idempotent if already structured.
    const migratedRolls = migrateAllEntries(validRolls);
    // [PHASE B / #049 — 2026-05-07]: Mirror the same data into the
    //   RollTablesTab schema (`entries` array of {minRange, maxRange,
    //   text}, `type: 'weighted'`, `category: 'custom'`) so the same
    //   table object surfaces in BOTH the shop's rarity-tier picker
    //   (which reads `rolls`) AND the global Roll Tables tab (which
    //   reads `entries` + uses `rollOnTable`). Pre-fix the dialog only
    //   produced the shop schema; tables created from the shop never
    //   reached the Roll Tables tab. Smoke-test report 2026-05-07.
    // [PHASE B / #054 — 2026-05-07]: Dual-write `type` AND `tableType`
    //   so the RollTablesTab editor (which reads `tableType`) sees the
    //   same value rollOnTable does (which reads `type`). Pre-fix,
    //   opening a #049-authored table in the global editor and toggling
    //   to weighted mode dropped the user's entries because the gate
    //   `editingTable?.tableType === 'weighted'` evaluated false.
    const newTable = {
      id: `custom_table_${Date.now()}`,
      name: name.trim(),
      description: description.trim(),
      type: 'weighted',
      tableType: 'weighted',
      category: 'custom',
      rolls: migratedRolls,
      entries: validRolls.map((r) => ({
        minRange: parseInt(r.min, 10),
        maxRange: parseInt(r.max, 10),
        text: r.item,
      })),
    };

    onCreate(newTable);
    // Reset state for next open
    setName('');
    setDescription('');
    setEntries([emptyEntry()]);
    onClose();
  }, [name, description, entries, onCreate, onClose]);

  if (!open) return null;

  return (
    <MobileModal
      isOpen={open}
      onClose={onClose}
      title="🎲 Create Custom Roll Table"
      onSave={handleSave}
      saveLabel="Create Table"
      overlayClassName="z-[100]"
      maxWidthClass="max-w-4xl"
    >
      <div className="space-y-4">
          <div>
            <label htmlFor="custom-table-name-input" className="block text-sm text-gray-300 mb-1">Table Name</label>
            <input
              id="custom-table-name-input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My Custom Loot Table"
              className="w-full p-2 bg-gray-700 text-white rounded"
            />
          </div>

          <div>
            <label htmlFor="custom-table-desc-input" className="block text-sm text-gray-300 mb-1">Description</label>
            <textarea
              id="custom-table-desc-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what this table is for..."
              className="w-full p-2 bg-gray-700 text-white rounded h-16"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm text-gray-300">Table Entries (d100)</div>
              {/* [PHASE B / #025]: Toggle only renders if at least one
                  inventory container actually has contents. Otherwise
                  it's noise — there's nothing to expand. */}
              {containersWithContents.length > 0 && (
                <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={showContainerContents}
                    onChange={(e) => setShowContainerContents(e.target.checked)}
                    className="accent-blue-500"
                  />
                  Show container contents
                </label>
              )}
            </div>
            <div className="space-y-2 max-h-60 overflow-y-auto p-2 bg-gray-700 rounded">
              <div className="grid grid-cols-12 gap-2 text-xs font-bold text-gray-400 mb-2">
                <div className="col-span-2">Min</div>
                <div className="col-span-2">Max</div>
                <div className="col-span-5">Item</div>
                <div className="col-span-2">Rarity</div>
                <div className="col-span-1"></div>
              </div>

              {entries.map((entry, idx) => (
                <div key={idx} data-testid="table-entry-row" className="grid grid-cols-12 gap-2">
                  <input
                    type="number"
                    min="1"
                    max="100"
                    placeholder={idx === 0 ? '1' : '51'}
                    aria-label={`Entry ${idx + 1} min`}
                    value={entry.min}
                    onChange={(e) => handleEntryChange(idx, 'min', e.target.value)}
                    className="col-span-2 p-1 bg-gray-800 rounded text-sm"
                  />
                  <input
                    type="number"
                    min="1"
                    max="100"
                    placeholder={idx === 0 ? '50' : '100'}
                    aria-label={`Entry ${idx + 1} max`}
                    value={entry.max}
                    onChange={(e) => handleEntryChange(idx, 'max', e.target.value)}
                    className="col-span-2 p-1 bg-gray-800 rounded text-sm"
                  />
                  <select
                    aria-label={`Entry ${idx + 1} item`}
                    value={entry.item}
                    onChange={(e) => handleEntryChange(idx, 'item', e.target.value)}
                    className="col-span-5 p-1 bg-gray-800 rounded text-sm"
                  >
                    <option value="">Select Item...</option>
                    {inventory.length > 0 && (
                      <optgroup label="📦 Your Inventory">
                        {/* [PHASE B / #025 — 2026-05-06]: Container heads
                            ALWAYS render here (top-level inventory). Pre-#025
                            this group flat-listed every container's
                            containedItems indented underneath each head;
                            with multiple containers the dropdown became
                            30+ rows long and the user reported it was
                            "crazy hard to navigate the scroll area."
                            Container contents now move to their own
                            per-container optgroups below, gated on
                            `showContainerContents`. */}
                        {inventory.map((item, invIdx) => (
                          <option key={`inv-${invIdx}`} value={`custom_inv_${invIdx}`}>
                            {item.icon || ''} {item.name} (Custom)
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {/* [PHASE B / #025]: Per-container optgroups expose
                        containedItems for authors who want to roll for
                        a specific contained item. Default-OFF
                        (showContainerContents=false) keeps the picker
                        short; toggle ON to surface them. The
                        `custom_inv_<idx>_contents_<sub>` dispatch
                        encoding from #018 is preserved unchanged so
                        `resolveCustomInvRef` keeps working. */}
                    {showContainerContents && containersWithContents.map(({ item, idx: invIdx }) => (
                      <optgroup
                        key={`inv-${invIdx}-contents`}
                        label={`↳ Inside ${item.name} (${item.containedItems.length} items)`}
                      >
                        {item.containedItems.map((sub, subIdx) => (
                          <option
                            key={`inv-${invIdx}-contents-${subIdx}`}
                            value={`custom_inv_${invIdx}_contents_${subIdx}`}
                          >
                            {sub.icon || ''} {sub.name}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                    {Object.entries(baseItems).map(([category, items]) => (
                      <optgroup key={category} label={category.replace(/_/g, ' ')}>
                        {Object.keys(items).map((itemName) => (
                          <option key={itemName} value={itemName}>
                            {itemName.replace(/_/g, ' ')}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  <select
                    aria-label={`Entry ${idx + 1} rarity`}
                    value={entry.rarity}
                    onChange={(e) => handleEntryChange(idx, 'rarity', e.target.value)}
                    className="col-span-2 p-1 bg-gray-800 rounded text-sm"
                  >
                    {RARITIES.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    aria-label={`Remove entry ${idx + 1}`}
                    onClick={() => handleRemoveEntry(idx)}
                    className="col-span-1 text-red-500 hover:text-red-400"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>

            <div className="mt-2 flex gap-2">
              <button
                type="button"
                onClick={handleAddEntry}
                className="text-xs bg-blue-700 hover:bg-blue-600 px-3 py-1 rounded flex-1"
              >
                + Add Entry
              </button>
              {/* [PHASE B / #049 — 2026-05-07]: Distribute Evenly mirrors
                  RollTablesTab's distributeRangesEvenly. Renders only with
                  ≥2 entries (single entry's range is always 1-100, no
                  distribution needed). */}
              {entries.length >= 2 && (
                <button
                  type="button"
                  onClick={handleDistributeEvenly}
                  className="text-xs bg-amber-800 hover:bg-amber-700 px-3 py-1 rounded"
                  title="Split 1-100 evenly across all entries"
                >
                  Distribute Evenly
                </button>
              )}
            </div>
          </div>

          <div className="bg-yellow-900 p-3 rounded">
            <p className="text-sm text-yellow-300">
              💡 Create a d100 roll table like the official DMG tables. Each entry defines a roll range (e.g., 1-50) and what item appears.
            </p>
          </div>
        </div>
    </MobileModal>
  );
}
